/**
 * src/services/weatherService.js
 * Service d'intégration de l'API météo Open-Meteo (100% gratuite, sans clé d'API).
 */

export const DEFAULT_LOCATION = {
  name: "Lagny-sur-Marne",
  postcode: "77400",
  admin1: "Île-de-France",
  admin2: "Seine-et-Marne",
  country: "France",
  latitude: 48.86667,
  longitude: 2.71667,
};

const STORAGE_KEY = "weather_location";
const inMemoryCache = new Map();
const CACHE_TTL_MS = 30 * 60 * 1000; // 30 minutes

/**
 * Récupère l'emplacement actuellement sauvegardé par l'utilisateur
 */
export function getSavedLocation() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      // Si l'utilisateur avait encore l'ancien défaut "Paris", basculer vers Lagny-sur-Marne
      if (parsed && (parsed.name === "Paris" || !parsed.name)) {
        saveLocation(DEFAULT_LOCATION);
        return DEFAULT_LOCATION;
      }
      return parsed;
    }
  } catch (e) {
    console.error("Erreur lecture weather_location", e);
  }
  return DEFAULT_LOCATION;
}

/**
 * Sauvegarde un nouvel emplacement
 */
export function saveLocation(loc) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(loc));
  } catch (e) {
    console.error("Erreur écriture weather_location", e);
  }
}

/**
 * Dictionnaire complet des codes WMO Open-Meteo
 */
export const WMO_WEATHER_MAP = {
  0: { label: "Ensoleillé", icon: "Sun", color: "text-amber-500", bg: "bg-amber-50", badgeBg: "bg-amber-100 text-amber-800 border-amber-200" },
  1: { label: "Généralement dégagé", icon: "Sun", color: "text-amber-500", bg: "bg-amber-50", badgeBg: "bg-amber-100 text-amber-800 border-amber-200" },
  2: { label: "Éclaircies", icon: "CloudSun", color: "text-amber-600", bg: "bg-amber-50", badgeBg: "bg-amber-50 text-amber-700 border-amber-200" },
  3: { label: "Couvert", icon: "Cloud", color: "text-gray-500", bg: "bg-gray-100", badgeBg: "bg-gray-100 text-gray-700 border-gray-200" },
  45: { label: "Brouillard", icon: "CloudFog", color: "text-slate-500", bg: "bg-slate-100", badgeBg: "bg-slate-100 text-slate-700 border-slate-200" },
  48: { label: "Brouillard givrant", icon: "CloudFog", color: "text-slate-600", bg: "bg-slate-100", badgeBg: "bg-slate-100 text-slate-700 border-slate-200" },
  51: { label: "Bruine légère", icon: "CloudDrizzle", color: "text-blue-400", bg: "bg-blue-50", badgeBg: "bg-blue-50 text-blue-700 border-blue-200" },
  53: { label: "Bruine modérée", icon: "CloudDrizzle", color: "text-blue-500", bg: "bg-blue-50", badgeBg: "bg-blue-50 text-blue-700 border-blue-200" },
  55: { label: "Bruine dense", icon: "CloudDrizzle", color: "text-blue-600", bg: "bg-blue-100", badgeBg: "bg-blue-100 text-blue-800 border-blue-300" },
  56: { label: "Bruine verglaçante légère", icon: "CloudSnow", color: "text-cyan-500", bg: "bg-cyan-50", badgeBg: "bg-cyan-50 text-cyan-800 border-cyan-200" },
  57: { label: "Bruine verglaçante dense", icon: "CloudSnow", color: "text-cyan-600", bg: "bg-cyan-100", badgeBg: "bg-cyan-100 text-cyan-800 border-cyan-300" },
  61: { label: "Pluie légère", icon: "CloudRain", color: "text-blue-500", bg: "bg-blue-50", badgeBg: "bg-blue-50 text-blue-700 border-blue-200" },
  63: { label: "Pluie modérée", icon: "CloudRain", color: "text-blue-600", bg: "bg-blue-100", badgeBg: "bg-blue-100 text-blue-800 border-blue-300" },
  65: { label: "Forte pluie", icon: "CloudRain", color: "text-blue-700", bg: "bg-blue-100", badgeBg: "bg-blue-200 text-blue-900 border-blue-300" },
  66: { label: "Pluie verglaçante légère", icon: "CloudSnow", color: "text-cyan-600", bg: "bg-cyan-50", badgeBg: "bg-cyan-50 text-cyan-800 border-cyan-200" },
  67: { label: "Pluie verglaçante forte", icon: "CloudSnow", color: "text-cyan-700", bg: "bg-cyan-100", badgeBg: "bg-cyan-100 text-cyan-900 border-cyan-300" },
  71: { label: "Neige légère", icon: "CloudSnow", color: "text-sky-500", bg: "bg-sky-50", badgeBg: "bg-sky-50 text-sky-800 border-sky-200" },
  73: { label: "Neige modérée", icon: "CloudSnow", color: "text-sky-600", bg: "bg-sky-100", badgeBg: "bg-sky-100 text-sky-800 border-sky-300" },
  75: { label: "Forte neige", icon: "CloudSnow", color: "text-sky-700", bg: "bg-sky-100", badgeBg: "bg-sky-200 text-sky-900 border-sky-300" },
  77: { label: "Grains de neige", icon: "CloudSnow", color: "text-sky-600", bg: "bg-sky-50", badgeBg: "bg-sky-50 text-sky-800 border-sky-200" },
  80: { label: "Averses légères", icon: "CloudRain", color: "text-indigo-500", bg: "bg-indigo-50", badgeBg: "bg-indigo-50 text-indigo-700 border-indigo-200" },
  81: { label: "Averses modérées", icon: "CloudRain", color: "text-indigo-600", bg: "bg-indigo-100", badgeBg: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  82: { label: "Averses violentes", icon: "CloudRain", color: "text-indigo-700", bg: "bg-indigo-100", badgeBg: "bg-indigo-200 text-indigo-900 border-indigo-300" },
  85: { label: "Averses de neige légères", icon: "CloudSnow", color: "text-sky-600", bg: "bg-sky-50", badgeBg: "bg-sky-50 text-sky-800 border-sky-200" },
  86: { label: "Averses de neige fortes", icon: "CloudSnow", color: "text-sky-700", bg: "bg-sky-100", badgeBg: "bg-sky-100 text-sky-900 border-sky-300" },
  95: { label: "Orage", icon: "CloudLightning", color: "text-purple-600", bg: "bg-purple-50", badgeBg: "bg-purple-100 text-purple-800 border-purple-300" },
  96: { label: "Orage avec grêle légère", icon: "CloudLightning", color: "text-purple-700", bg: "bg-purple-100", badgeBg: "bg-purple-200 text-purple-900 border-purple-300" },
  99: { label: "Orage avec forte grêle", icon: "CloudLightning", color: "text-purple-800", bg: "bg-purple-100", badgeBg: "bg-purple-200 text-purple-900 border-purple-300" },
};

export function getWeatherMeta(code) {
  return (
    WMO_WEATHER_MAP[code] || {
      label: "Variable",
      icon: "CloudSun",
      color: "text-gray-500",
      bg: "bg-gray-50",
      badgeBg: "bg-gray-100 text-gray-700 border-gray-200",
    }
  );
}

/**
 * Récupère les prévisions météo hebdomadaires et horaires depuis Open-Meteo
 * @param {string} startDateStr - YYYY-MM-DD
 * @param {string} endDateStr - YYYY-MM-DD
 * @param {object} location - { latitude, longitude, name }
 * @returns {Promise<{ dailyMap: Object, error?: string }>}
 */
export async function fetchWeekWeather(startDateStr, endDateStr, location = getSavedLocation()) {
  const lat = location?.latitude ?? DEFAULT_LOCATION.latitude;
  const lon = location?.longitude ?? DEFAULT_LOCATION.longitude;
  const cacheKey = `${lat.toFixed(4)}_${lon.toFixed(4)}_${startDateStr}_${endDateStr}`;

  // Vérifier le cache mémoire
  const cached = inMemoryCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL_MS) {
    return cached.data;
  }

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 6000);

    const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&start_date=${startDateStr}&end_date=${endDateStr}&hourly=temperature_2m,precipitation_probability,precipitation,weather_code&daily=weather_code,temperature_2m_max,temperature_2m_min,precipitation_probability_max,precipitation_sum&timezone=auto`;

    let res;
    try {
      res = await fetch(url, { signal: controller.signal });
    } finally {
      clearTimeout(timeoutId);
    }

    if (!res || !res.ok) {
      return {
        dailyMap: {},
        error: "Service météo temporairement indisponible.",
      };
    }

    const data = await res.json();

    if (!data || data.error) {
      return {
        dailyMap: {},
        error: data?.reason || "Météo non disponible pour cette période.",
      };
    }

    const dailyMap = {};
    const { daily, hourly } = data;

    if (daily && Array.isArray(daily.time)) {
      daily.time.forEach((dateKey, dayIdx) => {
        dailyMap[dateKey] = {
          date: dateKey,
          weatherCode: Array.isArray(daily.weather_code) ? daily.weather_code[dayIdx] : 0,
          maxTemp: Array.isArray(daily.temperature_2m_max) && daily.temperature_2m_max[dayIdx] !== null ? Math.round(daily.temperature_2m_max[dayIdx]) : null,
          minTemp: Array.isArray(daily.temperature_2m_min) && daily.temperature_2m_min[dayIdx] !== null ? Math.round(daily.temperature_2m_min[dayIdx]) : null,
          maxRainProb: Array.isArray(daily.precipitation_probability_max) ? (daily.precipitation_probability_max[dayIdx] ?? 0) : 0,
          rainSum: Array.isArray(daily.precipitation_sum) && daily.precipitation_sum[dayIdx] !== null ? Math.round(daily.precipitation_sum[dayIdx] * 10) / 10 : 0,
          hourly: [],
        };
      });
    }

    // Regrouper les heures par date
    if (hourly && Array.isArray(hourly.time)) {
      hourly.time.forEach((isoTime, hourIdx) => {
        if (!isoTime || typeof isoTime !== "string") return;
        const dateKey = isoTime.slice(0, 10);
        const hourStr = isoTime.slice(11, 16);

        if (dailyMap[dateKey]) {
          dailyMap[dateKey].hourly.push({
            time: isoTime,
            hour: hourStr,
            temp: Array.isArray(hourly.temperature_2m) && hourly.temperature_2m[hourIdx] !== null ? Math.round(hourly.temperature_2m[hourIdx]) : null,
            rainProb: Array.isArray(hourly.precipitation_probability) ? (hourly.precipitation_probability[hourIdx] ?? 0) : 0,
            rainMm: Array.isArray(hourly.precipitation) && hourly.precipitation[hourIdx] !== null ? Math.round(hourly.precipitation[hourIdx] * 10) / 10 : 0,
            weatherCode: Array.isArray(hourly.weather_code) ? (hourly.weather_code[hourIdx] ?? 0) : 0,
          });
        }
      });
    }

    const result = { dailyMap, location };
    inMemoryCache.set(cacheKey, { timestamp: Date.now(), data: result });
    return result;
  } catch (err) {
    // Si la requête échoue ou est interrompue, ne jamais faire crasher l'application
    return {
      dailyMap: {},
      error: "Service météo inaccessible.",
    };
  }
}

/**
 * Recherche de villes via l'API de géocodage gratuite d'Open-Meteo
 * @param {string} query - Nom de la ville (ex: "Paris", "Lyon")
 * @returns {Promise<Array>}
 */
export async function searchCities(query) {
  if (!query || query.trim().length < 2) return [];

  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 4000);

    const url = `https://geocoding-api.open-meteo.com/v1/search?name=${encodeURIComponent(
      query.trim()
    )}&count=6&language=fr&format=json`;

    let res;
    try {
      res = await fetch(url, { signal: controller.signal });
    } finally {
      clearTimeout(timeoutId);
    }

    if (!res || !res.ok) return [];

    const data = await res.json();

    if (!data || !Array.isArray(data.results)) return [];

    return data.results.map((item) => ({
      name: item.name,
      admin1: item.admin1 || "",
      country: item.country || "",
      latitude: item.latitude,
      longitude: item.longitude,
    }));
  } catch (err) {
    return [];
  }
}
